import { prepareSupabaseAuth } from "@/lib/bootstrapSupabaseAuth";
import "./instrument";
import { createRoot } from "react-dom/client";
import "./index.css";
import { initGlobalErrorHandler, logManualCrash } from "./lib/globalErrorHandler";
import { readDiagnosticRuntimeToggle } from "./lib/diagnosticRuntime";
import { sendLogWithKeepalive, flushPendingLogs } from "./lib/keepaliveLogger";
import { createReactWarningDeduper } from "./lib/reactWarningFilter";

const shouldSkipReactWarning = createReactWarningDeduper();
const originalConsoleWarn = console.warn.bind(console);
const originalConsoleError = console.error.bind(console);

function toConsoleMessage(args: unknown[]): string {
  return args
    .map((value) => (typeof value === "string" ? value : String(value)))
    .join(" ");
}

console.warn = (...args: unknown[]) => {
  const message = toConsoleMessage(args);
  if (shouldSkipReactWarning(message)) return;
  originalConsoleWarn(...args);
};

console.error = (...args: unknown[]) => {
  const message = toConsoleMessage(args);
  if (shouldSkipReactWarning(message)) return;
  originalConsoleError(...args);
};

// Canonical domain redirect - ensure consistent domain for session persistence
// Redirect mooncatcher.ai to www.mooncatcher.ai (or vice versa - pick one canonical)
const CANONICAL_HOST = 'www.mooncatcher.ai';
const currentHost = window.location.hostname;

if (currentHost === 'mooncatcher.ai') {
  // Redirect to canonical www version to ensure consistent session storage
  const newUrl = window.location.href.replace('://mooncatcher.ai', `://${CANONICAL_HOST}`);
  console.log(`[main] Redirecting to canonical host: ${newUrl}`);
  window.location.replace(newUrl);
}

// ── Reload Tracer: read black-box from previous session ──
const RELOAD_BLACKBOX_KEY = 'reload_tracer_blackbox';
if (readDiagnosticRuntimeToggle('reload_tracer')) try {
  const prev = sessionStorage.getItem(RELOAD_BLACKBOX_KEY);
  if (prev) {
    sessionStorage.removeItem(RELOAD_BLACKBOX_KEY);
    const data = JSON.parse(prev);
    console.warn(
      `[ReloadTracer] 🔴 Previous page unloaded at ${data.timestamp} (page alive ${Math.round(data.performanceNow)}ms)\n` +
      `  URL: ${data.url}\n` +
      `  Stack:\n${data.stack}`
    );
  }
} catch { /* ignore parse errors */ }

// ── Reload Tracer: record black-box on ANY unload ──
if (readDiagnosticRuntimeToggle('reload_tracer'))
window.addEventListener('beforeunload', () => {
  try {
    sessionStorage.setItem(RELOAD_BLACKBOX_KEY, JSON.stringify({
      timestamp: new Date().toISOString(),
      performanceNow: performance.now(),
      url: window.location.href,
      stack: new Error('[ReloadTracer] beforeunload stack').stack,
    }));
  } catch { /* quota / private mode */ }
});

// Log startup marker BEFORE anything else (for crash correlation)
const startupTimestamp = new Date().toISOString();
console.log(`[main] App startup at ${startupTimestamp}`);

// Send startup beacon for crash correlation using CORS-safe fetch
const sendStartupBeacon = () => {
  const payload = {
    error_message: '[STARTUP] App initialization started',
    error_stack: null,
    url: window.location.href,
    user_agent: navigator.userAgent,
    severity: 'info',
    source: 'AppStartup',
    error_type: 'startup_event',
    metadata: {
      type: 'startup_marker',
      timestamp: startupTimestamp,
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
    },
  };
  
  // Use CORS-safe keepalive fetch instead of sendBeacon
  sendLogWithKeepalive(payload).catch(() => {
    // Silent fail - startup beacon is optional
  });
};

// Flush any pending logs from previous sessions
flushPendingLogs();

// Send startup beacon in preview mode for debugging
if (readDiagnosticRuntimeToggle('startup_beacon') && (window.location.hostname.includes('lovableproject.com') || window.location.hostname === 'localhost')) {
  sendStartupBeacon();
}

// Initialize global error handler for mobile crash logging
initGlobalErrorHandler();

// Chunk load failure recovery - catches stale cached chunks after deploys
const CHUNK_RETRY_KEY = 'chunk_reload_attempted';
const PAGE_LOAD_TIME = Date.now();
const RELOAD_GUARD_MS = 10_000; // Skip reload if page loaded < 10s ago

window.addEventListener('error', (event) => {
  if (!readDiagnosticRuntimeToggle('chunk_recovery')) return;
  const msg = event.message || '';
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    // If the error originates from a widget module, let the widget-level
    // retry handler deal with it instead of reloading the whole page.
    const failedUrl = (event as any).filename || msg;
    const isWidgetModule = failedUrl.includes('/widgets/') || failedUrl.includes('/trading/');
    if (isWidgetModule) {
      console.warn('[main] Chunk error from widget module — skipping page reload, widget-level retry will handle it');
      return;
    }

    const alreadyRetried = sessionStorage.getItem(CHUNK_RETRY_KEY);
    const msSinceLoad = Date.now() - PAGE_LOAD_TIME;

    // Guard: if the page just loaded, skip the reload to prevent loops
    if (msSinceLoad < RELOAD_GUARD_MS) {
      console.warn(`[main] Chunk error ignored — page loaded ${msSinceLoad}ms ago (guard: ${RELOAD_GUARD_MS}ms)`);
      return;
    }

    if (!alreadyRetried) {
      console.log('[main] RELOAD triggered by: chunk_recovery');
      sessionStorage.setItem(CHUNK_RETRY_KEY, '1');
      // Clear the JS service worker cache
      if (typeof caches !== 'undefined') {
        caches.delete('js-cache').finally(() => {
          location.reload();
        });
      } else {
        location.reload();
      }
    } else {
      console.warn('[main] Chunk load failed after retry, not reloading again');
      sessionStorage.removeItem(CHUNK_RETRY_KEY);
    }
  }
});
// Clear retry flag on successful load
window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RETRY_KEY);
});

// PWA service worker is now registered automatically by vite-plugin-pwa
// Only log when service worker is successfully registered
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.ready.then(registration => {
    console.log('[main] Service worker ready for offline support');
  }).catch(err => {
    console.warn('[main] Service worker not available:', err);
  });
}

// iOS and mobile-specific mounting delay to prevent memory exhaustion
const isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isMobile: boolean = window.innerWidth < 768;

let hasMounted = false;

const mountApp = async () => {
  if (hasMounted) return;
  hasMounted = true;

  try {
    const rootEl = document.getElementById("root");
    if (!rootEl) {
      console.error('[Mount Error] #root element not found');
      return;
    }

    await prepareSupabaseAuth();
    const { default: App } = await import("./App.tsx");

    createRoot(rootEl).render(<App />);
    console.log('[main] React app mounted successfully');
  } catch (err) {
    console.error('[Mount Error]', err);
    logManualCrash(
      err instanceof Error ? err.message : String(err),
      'main.tsx:mount',
      { stack: err instanceof Error ? err.stack : undefined }
    );
  }
};

// Delay on mobile to ensure browser is ready (prevents white screen crashes)
if (isIOS || isMobile) {
  // Longer delay on mobile - let browser settle before React mount
  const delay = isIOS ? 300 : 200;
  setTimeout(mountApp, delay);
} else {
  mountApp();
}
